import { createContext, type FC, type ReactNode, useContext, useMemo, useState } from 'react';

import { useNavigate } from '@remix-run/react';

import { DeletePostDialog } from '../_components/ui/DeletePostDialog';
import { MovePostDialog, type MoveTarget } from '../_components/ui/MovePostDialog';

import { useDeletePostMutation } from '../_hooks/useDeletePostMutation';

interface DeleteTarget {
  postId: string;
  isModeratorRemoval: boolean;
  isSyncedWithWebsite?: boolean;
  backTo?: string;
}

interface CommunityPostActions {
  openDeleteDialog: (target: DeleteTarget) => void;
  openMoveDialog: (target: MoveTarget) => void;
}

const CommunityPostActionsContext = createContext<CommunityPostActions | null>(null);

// biome-ignore lint/style/useComponentExportOnlyModules: provider co-locates its context hook by design
export const useCommunityPostActions = (): CommunityPostActions => {
  const ctx = useContext(CommunityPostActionsContext);
  if (!ctx) throw new Error('useCommunityPostActions must be used within CommunityPostActionsProvider');
  return ctx;
};

// One active-dialog atom keeps the provider flat as actions are added: each new action is a
// variant (`kind` + its own target payload), not another cluster of useState.
type ActiveDialog = { kind: 'delete'; target: DeleteTarget } | { kind: 'move'; target: MoveTarget };

export const CommunityPostActionsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const deleteMutation = useDeletePostMutation();
  const [activeDialog, setActiveDialog] = useState<ActiveDialog | null>(null);
  // `isOpen` drives the fade independently of `activeDialog`: closing flips `isOpen` to play
  // the leave transition, and the target is cleared only after it (`afterClose`).
  const [isOpen, setIsOpen] = useState(false);

  const closeDialog = () => setIsOpen(false);
  const afterClose = () => setActiveDialog(null);

  const value = useMemo<CommunityPostActions>(
    () => ({
      openDeleteDialog: (target) => {
        setActiveDialog({ kind: 'delete', target });
        setIsOpen(true);
      },
      openMoveDialog: (target) => {
        setActiveDialog({ kind: 'move', target });
        setIsOpen(true);
      },
    }),
    [],
  );

  const actionableTarget = activeDialog?.kind === 'delete' ? activeDialog.target : null;
  const moveTarget = activeDialog?.kind === 'move' ? activeDialog.target : null;

  const handleConfirmDelete = (reason?: string) => {
    if (!actionableTarget) return;
    deleteMutation.mutate(
      {
        postId: actionableTarget.postId,
        isModeratorRemoval: actionableTarget.isModeratorRemoval,
        reason: actionableTarget.isModeratorRemoval ? reason : undefined,
      },
      {
        // Keep the dialog open (with its spinner) until the request settles, so an error
        // rollback leaves the user a retry path instead of a dismissed dialog.
        onSuccess: () => {
          const { backTo } = actionableTarget;
          closeDialog();
          // On the post-detail page (`backTo` set) the post is gone, so navigate away; on the
          // feed there's no `backTo` — the optimistic cache removal hides it in place.
          if (backTo) navigate(backTo);
        },
      },
    );
  };

  return (
    <CommunityPostActionsContext.Provider value={value}>
      {children}

      <DeletePostDialog
        isOpen={isOpen && activeDialog?.kind === 'delete'}
        isModeratorRemoval={actionableTarget?.isModeratorRemoval ?? false}
        isSyncedWithWebsite={actionableTarget?.isSyncedWithWebsite ?? false}
        postId={actionableTarget?.postId}
        onConfirm={handleConfirmDelete}
        onCancel={closeDialog}
        onClosed={afterClose}
        isLoading={deleteMutation.isPending}
      />

      <MovePostDialog
        isOpen={isOpen && activeDialog?.kind === 'move'}
        target={moveTarget}
        onMoved={closeDialog}
        onCancel={closeDialog}
        onClosed={afterClose}
      />
    </CommunityPostActionsContext.Provider>
  );
};
